/* global axios */
import ApiClient from './ApiClient';

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban_boards', { accountScoped: true });
  }

  // Boards
  getBoards() {
    return axios.get(this.url);
  }

  getBoard(boardId) {
    return axios.get(`${this.url}/${boardId}`);
  }

  createBoard(data) {
    return axios.post(this.url, data);
  }

  updateBoard(boardId, data) {
    return axios.patch(`${this.url}/${boardId}`, data);
  }

  deleteBoard(boardId) {
    return axios.delete(`${this.url}/${boardId}`);
  }

  // Columns
  getColumns(boardId) {
    return axios.get(`${this.url}/${boardId}/kanban_columns`);
  }

  createColumn(boardId, data) {
    return axios.post(`${this.url}/${boardId}/kanban_columns`, data);
  }

  updateColumn(boardId, columnId, data) {
    return axios.patch(
      `${this.url}/${boardId}/kanban_columns/${columnId}`,
      data
    );
  }

  deleteColumn(boardId, columnId) {
    return axios.delete(`${this.url}/${boardId}/kanban_columns/${columnId}`);
  }

  reorderColumn(boardId, columnId, position) {
    return axios.patch(
      `${this.url}/${boardId}/kanban_columns/${columnId}/reorder`,
      { position }
    );
  }

  reorderColumns(boardId, columnIds) {
    return axios.patch(
      `${this.url}/${boardId}/kanban_columns/reorder_all`,
      { column_ids: columnIds }
    );
  }

  // Column Automations
  getAutomations(boardId, columnId) {
    return axios.get(
      `${this.url}/${boardId}/kanban_columns/${columnId}/automations`
    );
  }

  createAutomation(boardId, columnId, data) {
    return axios.post(
      `${this.url}/${boardId}/kanban_columns/${columnId}/automations`,
      { automation: data }
    );
  }

  updateAutomation(boardId, columnId, automationId, data) {
    return axios.patch(
      `${this.url}/${boardId}/kanban_columns/${columnId}/automations/${automationId}`,
      { automation: data }
    );
  }

  deleteAutomation(boardId, columnId, automationId) {
    return axios.delete(
      `${this.url}/${boardId}/kanban_columns/${columnId}/automations/${automationId}`
    );
  }

  bulkUpdateAutomations(boardId, columnId, automations) {
    return axios.put(
      `${this.url}/${boardId}/kanban_columns/${columnId}/automations/bulk_update`,
      { automations }
    );
  }

  // Board Automations
  getBoardAutomations(boardId) {
    return axios.get(`${this.url}/${boardId}/automations`);
  }

  createBoardAutomation(boardId, data) {
    return axios.post(`${this.url}/${boardId}/automations`, data);
  }

  updateBoardAutomation(boardId, automationId, data) {
    return axios.patch(`${this.url}/${boardId}/automations/${automationId}`, data);
  }

  deleteBoardAutomation(boardId, automationId) {
    return axios.delete(`${this.url}/${boardId}/automations/${automationId}`);
  }

  reorderBoardAutomations(boardId, automationIds) {
    return axios.patch(`${this.url}/${boardId}/automations/reorder`, {
      automation_ids: automationIds,
    });
  }

  // Cards
  getCards(boardId) {
    return axios.get(`${this.baseUrl()}/kanban_cards`, {
      params: { kanban_board_id: boardId },
    });
  }

  // Paginated cards for a single column (infinite scroll)
  getColumnCards(columnId, { limit = 20, offset = 0, sort, filters = {} } = {}) {
    return axios.get(`${this.baseUrl()}/kanban_cards`, {
      params: { kanban_column_id: columnId, limit, offset, sort, ...filters },
    });
  }

  // Paginated cards for multiple columns at once (combined boards view)
  getCardsForColumns(columnIds, { limit = 20, offset = 0, filters = {} } = {}) {
    return axios.get(`${this.baseUrl()}/kanban_cards`, {
      params: { column_ids: columnIds, limit, offset, ...filters },
    });
  }

  // Full-text search across boards the user can access
  searchCards(boardIds, query) {
    return axios.get(`${this.baseUrl()}/kanban_cards/search`, {
      params: { board_ids: boardIds, q: query },
    });
  }

  getCardByConversation(conversationId) {
    return axios.get(
      `${this.baseUrl()}/kanban_cards/by_conversation/${conversationId}`
    );
  }

  moveCard(cardId, targetColumnId, position) {
    return axios.patch(`${this.baseUrl()}/kanban_cards/${cardId}/move`, {
      target_column_id: targetColumnId,
      position,
    });
  }

  deleteCard(cardId) {
    return axios.delete(`${this.baseUrl()}/kanban_cards/${cardId}`);
  }

  clearBotError(cardId) {
    return axios.post(`${this.baseUrl()}/kanban_cards/${cardId}/clear_bot_error`);
  }

  resendToBot(cardId) {
    return axios.post(`${this.baseUrl()}/kanban_cards/${cardId}/resend_to_bot`);
  }
}

export default new KanbanAPI();

